import { Button, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import dayjs from 'dayjs';

import { type IncorporationCompany } from '@hooks/api';
import { getDateDefault } from '@utils';

const { Text } = Typography;

/**
 * `2026-08-01T09:12:44Z` → `01.08.2026`, and anything absent or unparseable → an em dash.
 *
 * Through dayjs rather than `new Date(...).toLocaleDateString()`, because `incorporationDate` is a
 * date-only string and `new Date('2026-08-14')` is parsed as UTC midnight — which renders as the
 * 13th for any viewer west of Greenwich, right beside the date field the CSM may be about to fill
 * in. dayjs parses a bare date as local midnight. `getDateDefault` also defaults an empty input to
 * *today*, so the null guard has to come first.
 */
export const formatDate = (value: string | null): string => {
  if (!value) return '—';

  return dayjs(value).isValid() ? getDateDefault(value) : '—';
};

/**
 * The queue's columns.
 *
 * Deliberately Master Data's own fields and nothing derived: this page is a window onto MDS, and a
 * CSM comparing it against the MDS admin has to see the same values. The one column that is not
 * MDS's is the last — whether legacy already holds the company, which is the whole reason the page
 * can be pressed twice safely.
 */
/** A queue row, plus the handler the table binds so the trailing button can open it. */
export type IncorporationRow = IncorporationCompany & { onOpen: () => void };

export const columns: ColumnsType<IncorporationRow> = [
  {
    title: 'Company',
    dataIndex: 'legalName',
    key: 'legalName',
    render: (legalName: string, row) => (
      <>
        <div>{legalName}</div>
        {row.tradeName && row.tradeName !== legalName && (
          <Text type="secondary">{row.tradeName}</Text>
        )}
      </>
    ),
  },
  {
    title: 'Client №',
    dataIndex: 'clientNumber',
    key: 'clientNumber',
    width: 110,
    // MDS displays it grouped ("950 000"), so the queue does too — a CSM reads it off both.
    render: (clientNumber: number | null) =>
      clientNumber === null ? '—' : String(clientNumber).replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
  },
  {
    title: 'Code',
    dataIndex: 'companyCode',
    key: 'companyCode',
    width: 100,
    render: (companyCode: string | null) => companyCode || '—',
  },
  {
    title: 'Legal form',
    dataIndex: 'legalForm',
    key: 'legalForm',
    width: 120,
    render: (legalForm: string | null) => legalForm || '—',
  },
  {
    title: 'RCS',
    dataIndex: 'rcsNumber',
    key: 'rcsNumber',
    width: 110,
    render: (rcsNumber: string | null) => rcsNumber || '—',
  },
  {
    title: 'VAT',
    dataIndex: 'vatNumber',
    key: 'vatNumber',
    width: 140,
    render: (vatNumber: string | null) => vatNumber || '—',
  },
  {
    title: 'In MDS since',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 130,
    render: formatDate,
  },
  {
    title: 'Legacy',
    dataIndex: 'legacyCompanyId',
    key: 'legacyCompanyId',
    width: 170,
    // Three states, and collapsing the middle two would be the expensive mistake. Most of what sits
    // in `incorporating` is an existing client the legacy sync mirrored into MDS — reading that as
    // "not provisioned" invites a CSM to create a second tenant for somebody who already has one.
    render: (_: string | null, row) => {
      if (row.legacyMatchedBy === 'reference_num') {
        return (
          // `legacyMatchedBy` and `referenceNum` are independent nullable fields, so the reference
          // is not guaranteed to be there just because the match was by one.
          <Tooltip
            title={`Already a client in legacy${row.referenceNum ? ` (reference ${row.referenceNum})` : ''}. Nothing new will be created.`}
          >
            <Tag color="processing">Existing client</Tag>
          </Tooltip>
        );
      }

      if (row.legacyMatchedBy === 'idempotency_key') {
        return (
          <Tooltip title="Provisioned from this page, but MDS still says incorporating. Open the row to retry the status change.">
            <Tag color="warning">Needs MDS sync</Tag>
          </Tooltip>
        );
      }

      return <Tag>Not provisioned</Tag>;
    },
  },
  {
    title: 'Compliance',
    dataIndex: 'complianceApproved',
    key: 'complianceApproved',
    width: 150,
    /**
     * Whether compliance has approved the company, shown before a CSM opens the row.
     *
     * Not the same as "the action will be available": provisioning also needs somebody who can own
     * the company, so an Approved row can still open to a disabled button. This column answers the
     * one question a list can answer — the drawer explains the rest.
     *
     * The point of the column is the refusal, not the approval: without it the only way to learn
     * that a company cannot be taken into service is to open it and find the button disabled, one
     * company at a time. Three states, because "not approved" and "we could not ask" send a CSM to
     * two different people.
     *
     * `=== true` on both flags: a backend deployed before them sends nothing, and an absent value
     * must not be read as an answer. It lands on "awaiting approval", which is the safe side — the
     * provisioning route would refuse either way.
     */
    render: (_: boolean | undefined, row) => {
      if (row.complianceUnavailable === true) {
        return (
          <Tooltip title="The compliance service could not be reached, so approval cannot be confirmed. Provisioning is blocked until it can.">
            <Tag color="default">Compliance unavailable</Tag>
          </Tooltip>
        );
      }

      if (row.complianceApproved === true) {
        return <Tag color="success">Approved</Tag>;
      }

      return (
        <Tooltip title="No compliance officer has approved this company's case yet, so it cannot be taken into service.">
          <Tag color="warning">Awaiting approval</Tag>
        </Tooltip>
      );
    },
  },
  {
    title: '',
    key: 'open',
    width: 90,
    /**
     * The row's `onClick` is a shortcut, not the way in. A `<tr>` takes no focus and carries no key
     * handler, so without a real control here the only action on this page is unreachable from the
     * keyboard. `onOpen` is bound per row by the table.
     */
    render: (_: unknown, row) => (
      <Button size="small" onClick={() => row.onOpen()}>
        Open
      </Button>
    ),
  },
];

/**
 * When something last happened, as `01.08.2026 09:12`, or an em dash.
 *
 * With the time, unlike `formatDate`: these two columns are read against each other — did Master
 * Data move after we filled it — and a run and a change on the same day are the common case.
 */
export const formatMoment = (value: string | null | undefined): string => {
  if (!value) return '—';

  const moment = dayjs(value);

  return moment.isValid() ? moment.format('DD.MM.YYYY HH:mm') : '—';
};

/**
 * The provisioned view's columns.
 *
 * The queue's, minus compliance and plus the two timestamps the view exists to compare. The
 * compliance column goes because these companies are already in service: the gate it reports on
 * guards a transition they have made, and a column nobody can act on is noise in a list whose
 * whole point is 'these need a refresh'.
 */
export const provisionedColumns: ColumnsType<IncorporationRow> = [
  ...columns.filter((column) => column.key !== 'complianceApproved' && column.key !== 'open'),
  {
    title: 'Changed in MDS',
    dataIndex: 'lastActivityAt',
    key: 'lastActivityAt',
    render: (value: string | null | undefined) => formatMoment(value),
  },
  {
    title: 'Synced up to',
    dataIndex: 'activitySeenAt',
    key: 'activitySeenAt',
    /**
     * Where the last fill got to in Master Data's history, which is what the column beside it is
     * read against: the same value in both means there is nothing to bring over.
     *
     * "Never" is not 'unknown' — it is a company nothing has been pulled for since this was
     * recorded, which is why the row is on this list at all.
     */
    render: (value: string | null | undefined) =>
      value ? (
        formatMoment(value)
      ) : (
        <Tooltip title="No fill has recorded how far it got, so everything Master Data holds is unseen.">
          <Text type="secondary">Never</Text>
        </Tooltip>
      ),
  },
  ...columns.filter((column) => column.key === 'open'),
];
